import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { Subject, takeUntil, finalize } from 'rxjs';
import {
  ExternalClientTypeService,
  ExternalClientTypeListItemDto,
} from '../../services/externalclienttype.service';
import { ExternalClientTypeDialogComponent } from '../../components/externalclienttype-dialog/externalclienttype-dialog.component';

@Component({
  selector: 'app-externalclienttype',
  standalone: true,
  imports: [
    CommonModule,
    DatePipe,
    TableModule,
    ButtonModule,
    CardModule,
    ToastModule,
    TooltipModule,
    DynamicDialogModule,
  ],
  providers: [DialogService, MessageService],
  template: `
    <p-toast></p-toast>

    <div class="page-container">
      <p-card styleClass="main-card">
        <ng-template pTemplate="header">
          <div class="card-header">
            <h2>
              <i class="pi pi-list"></i>
              Tipos de Cliente Externo
            </h2>
            <p-button
              label="Novo Tipo"
              icon="pi pi-plus"
              (onClick)="openCreateDialog()"
              [disabled]="loading"
            ></p-button>
          </div>
        </ng-template>

        <ng-template pTemplate="content">
          @if (loading && dataSource.length === 0) {
            <div class="loading-container">
              <i class="pi pi-spin pi-spinner" style="font-size: 2rem"></i>
              <p>Carregando...</p>
            </div>
          } @else if (dataSource.length === 0) {
            <div class="empty-state">
              <i class="pi pi-info-circle empty-icon"></i>
              <p>Nenhum tipo de cliente externo cadastrado.</p>
              <p-button
                label="Criar Primeiro Tipo"
                icon="pi pi-plus"
                (onClick)="openCreateDialog()"
                styleClass="p-button-outlined"
              ></p-button>
            </div>
          } @else {
            <p-table
              [value]="dataSource"
              [paginator]="true"
              [rows]="10"
              [rowsPerPageOptions]="[5, 10, 20, 50]"
              [loading]="loading"
              responsiveLayout="scroll"
              styleClass="p-datatable-sm"
              [tableStyle]="{ 'min-width': '50rem' }"
            >
              <ng-template pTemplate="header">
                <tr>
                  <th style="width: 4rem">#</th>
                  <th>Nome</th>
                  <th>Descrição</th>
                  <th style="width: 12rem">Criado em</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-element let-i="rowIndex">
                <tr>
                  <td>{{ element.id }}</td>
                  <td>
                    <strong>{{ element.name }}</strong>
                  </td>
                  <td>{{ element.description || '-' }}</td>
                  <td>{{ element.createdAt | date: 'dd/MM/yyyy HH:mm' }}</td>
                </tr>
              </ng-template>
            </p-table>
          }
        </ng-template>
      </p-card>
    </div>
  `,
  styles: [`
    .page-container {
      padding: 16px;
    }

    .card-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px 20px;
      background: var(--surface-50, #f8f9fa);
      border-bottom: 1px solid var(--surface-200, #dee2e6);
    }

    .card-header h2 {
      margin: 0;
      font-size: 1.25rem;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .loading-container,
    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 48px 16px;
      gap: 12px;
      color: var(--text-color-secondary, #6c757d);
    }

    .empty-icon {
      font-size: 3rem;
      color: var(--surface-400, #ced4da);
    }

    :host ::ng-deep .main-card {
      border-radius: 8px;
      overflow: hidden;
    }

    :host ::ng-deep .main-card .p-card-body {
      padding: 0;
    }

    :host ::ng-deep .main-card .p-card-content {
      padding: 0;
    }
  `],
})
export class ExternalClientTypeComponent implements OnInit, OnDestroy {
  dataSource: ExternalClientTypeListItemDto[] = [];
  loading = false;
  private destroyed$ = new Subject<void>();

  constructor(
    private service: ExternalClientTypeService,
    private dialogService: DialogService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadTypes();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  loadTypes(): void {
    this.loading = true;
    this.service
      .list()
      .pipe(
        takeUntil(this.destroyed$),
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (data) => {
          this.dataSource = data;
          this.cdr.markForCheck();
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Falha ao carregar os tipos de cliente externo.',
            life: 5000,
          });
        },
      });
  }

  openCreateDialog(): void {
    const ref = this.dialogService.open(ExternalClientTypeDialogComponent, {
      header: 'Novo Tipo de Cliente Externo',
      width: '500px',
      modal: true,
      closable: true,
      closeOnEscape: true,
    });

    ref.onClose.pipe(takeUntil(this.destroyed$)).subscribe((result) => {
      if (result) {
        this.dataSource = [...this.dataSource, result];
        this.cdr.markForCheck();
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: `Tipo "${result.name}" adicionado à lista.`,
          life: 3000,
        });
      }
    });
  }
}
